import { FC, useMemo, useState } from 'react';
import { Box, Checkbox, Collapse, FormControlLabel, OutlinedInput, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import type { DiscoverTourCompany, DiscoverTourFilters } from '../../../../../types/tour.types';
import { sanitizePriceFilterInput } from '../../../../../utils/tourSearch.utils';
import filterStyles from '../FlightFilters/FlightFilters.module.scss';
import styles from './DiscoverFilters.module.scss';

type DiscoverFiltersProps = {
  filters: DiscoverTourFilters;
  onFiltersChange: (next: DiscoverTourFilters) => void;
  companies: DiscoverTourCompany[];
};

const FilterCheckboxUnchecked = () => (
  <span
    className={filterStyles.filterCheckboxUnchecked}
    aria-hidden
  />
);

const FilterCheckboxChecked = () => (
  <span
    className={filterStyles.filterCheckboxChecked}
    aria-hidden
  >
    <span className={filterStyles.filterCheckboxCheckmark} />
  </span>
);

const DiscoverFilters: FC<DiscoverFiltersProps> = ({ filters, onFiltersChange, companies }) => {
  const [companySectionOpen, setCompanySectionOpen] = useState(true);
  const [priceSectionOpen, setPriceSectionOpen] = useState(true);

  const visibleCompanies = useMemo(() => {
    const q = filters.companySearch.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c => c.name.toLowerCase().includes(q));
  }, [filters.companySearch, companies]);

  const handleCompanyToggle = (companyId: string) => {
    const nextIds = filters.companyIds.includes(companyId)
      ? filters.companyIds.filter(id => id !== companyId)
      : [...filters.companyIds, companyId];
    onFiltersChange({
      ...filters,
      companyIds: nextIds,
    });
  };

  const handlePriceMinChange = (raw: string) => {
    onFiltersChange({
      ...filters,
      priceRange: {
        ...filters.priceRange,
        min: sanitizePriceFilterInput(raw),
      },
    });
  };

  const handlePriceMaxChange = (raw: string) => {
    onFiltersChange({
      ...filters,
      priceRange: {
        ...filters.priceRange,
        max: sanitizePriceFilterInput(raw),
      },
    });
  };

  const sectionHeader = (open: boolean, onToggle: () => void, title: string) => (
    <Box
      className={`${filterStyles.filterHeader} ${open ? filterStyles.filterHeaderExpanded : ''}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <Typography
        component="span"
        className={filterStyles.filterTitle}
      >
        {title}
      </Typography>
      <KeyboardArrowDownIcon
        className={`${filterStyles.expandIcon} ${open ? filterStyles.expandIconOpen : ''}`}
        aria-hidden
      />
    </Box>
  );

  return (
    <Box className={filterStyles.filtersContainer}>
      <Box className={filterStyles.filterSection}>
        {sectionHeader(companySectionOpen, () => setCompanySectionOpen(v => !v), 'Tour Company')}

        <Collapse
          in={companySectionOpen}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.companySearchWrap}>
            <OutlinedInput
              fullWidth
              placeholder="Search"
              value={filters.companySearch}
              onChange={e =>
                onFiltersChange({
                  ...filters,
                  companySearch: e.target.value,
                })
              }
              onClick={e => e.stopPropagation()}
              className={styles.companySearchInput}
            />
          </Box>

          <Box className={filterStyles.checkboxGroup}>
            {visibleCompanies.length === 0 ? (
              <Typography
                variant="h11"
                sx={{ color: '#5E6A81', px: 0.5, py: 1 }}
              >
                No tour companies in these results.
              </Typography>
            ) : (
              visibleCompanies.map(company => (
                <FormControlLabel
                  key={company.id}
                  className={filterStyles.checkboxRow}
                  control={
                    <Checkbox
                      disableRipple
                      checked={filters.companyIds.includes(company.id)}
                      onChange={() => handleCompanyToggle(company.id)}
                      className={filterStyles.filterCheckbox}
                      size="small"
                      icon={<FilterCheckboxUnchecked />}
                      checkedIcon={<FilterCheckboxChecked />}
                    />
                  }
                  label={<span className={filterStyles.checkboxLabel}>{company.name}</span>}
                />
              ))
            )}
          </Box>
        </Collapse>
      </Box>

      <Box className={filterStyles.filterSection}>
        {sectionHeader(priceSectionOpen, () => setPriceSectionOpen(v => !v), 'Price Range')}

        <Collapse
          in={priceSectionOpen}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={filterStyles.timeRangeInputs}>
            <Box className={filterStyles.timeInput}>
              <Typography
                component="span"
                className={filterStyles.timeLabel}
              >
                Min
              </Typography>
              <OutlinedInput
                fullWidth
                placeholder="Any"
                value={filters.priceRange.min}
                onChange={e => handlePriceMinChange(e.target.value)}
                onClick={e => e.stopPropagation()}
                className={styles.priceFilterInput}
                inputProps={{ inputMode: 'decimal', min: 0 }}
              />
            </Box>
            <Box className={filterStyles.timeInput}>
              <Typography
                component="span"
                className={filterStyles.timeLabel}
              >
                Max
              </Typography>
              <OutlinedInput
                fullWidth
                placeholder="Any"
                value={filters.priceRange.max}
                onChange={e => handlePriceMaxChange(e.target.value)}
                onClick={e => e.stopPropagation()}
                className={styles.priceFilterInput}
                inputProps={{ inputMode: 'decimal', min: 0 }}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default DiscoverFilters;
